import { SWRConfiguration } from 'swr';

/**
 * Header name for admin business context.
 * When an admin is viewing a specific business, this header is sent.
 */
export const ADMIN_BUSINESS_CONTEXT_HEADER = 'x-admin-business-context';

/**
 * Session storage key for admin selected business.
 */
const ADMIN_SELECTED_BUSINESS_KEY = 'admin_selected_business';

/**
 * Gets the admin's selected business ID from session storage.
 * Returns null if not set or not in browser.
 */
export function getAdminSelectedBusinessId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem(ADMIN_SELECTED_BUSINESS_KEY);
  } catch {
    return null;
  }
}

/**
 * Creates headers object with admin context if available.
 * Use this when making API calls that need admin business context.
 */
export function getAdminContextHeaders(): Record<string, string> {
  const adminBusinessId = getAdminSelectedBusinessId();
  if (adminBusinessId) {
    return { [ADMIN_BUSINESS_CONTEXT_HEADER]: adminBusinessId };
  }
  return {};
}

/**
 * Enhanced fetch that includes admin business context header.
 * Use this for all API calls to ensure admin context is passed.
 */
export async function fetchWithAdminContext(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const adminHeaders = getAdminContextHeaders();
  const headers = new Headers(options.headers);

  // Add admin context header if present
  Object.entries(adminHeaders).forEach(([key, value]) => {
    headers.set(key, value);
  });

  return fetch(url, {
    ...options,
    headers,
  });
}

/**
 * Global SWR configuration for the app.
 * Provides intelligent caching, deduplication, and revalidation.
 *
 * OPTIMIZATION STRATEGY:
 * - Broadcast invalidation provides <100ms updates for mutations
 * - Real-time subscriptions catch database changes from any source
 * - revalidateOnFocus enabled as a safety net when realtime fails silently
 * - This ensures data freshness even if websocket connection drops
 */
export const swrConfig: SWRConfiguration = {
  // Enable revalidate on focus as a fallback when realtime fails silently
  // This ensures users get fresh data when they return to the tab
  revalidateOnFocus: true,

  // Revalidate when network reconnects (useful for mobile)
  revalidateOnReconnect: true,

  // Revalidate when data is marked stale (needed for invalidation to work)
  revalidateIfStale: true,

  // Ultra-short dedupe interval for instant updates after user actions
  dedupingInterval: 100, // 100ms - allows near-instant re-fetches

  // Keep previous data while fetching new data (smooth transitions)
  keepPreviousData: true,

  // Retry failed requests with exponential backoff
  shouldRetryOnError: true,
  errorRetryCount: 2,
  errorRetryInterval: 2000,

  // Focus throttle - revalidate at most once per 30 seconds when refocusing
  // This provides safety net without excessive requests
  focusThrottleInterval: 30000,

  // Compare function to prevent unnecessary re-renders
  // Fast shallow equality check - avoids expensive JSON.stringify on large datasets
  compare: (a, b) => {
    if (a === b) return true;
    if (!a || !b) return false;

    // For arrays (bookings, customers, etc.) - SWR maintains stable references
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a === b; // Reference equality (SWR preserves references when data hasn't changed)
    }

    // For objects, shallow compare keys
    if (typeof a === 'object' && typeof b === 'object') {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);
      if (keysA.length !== keysB.length) return false;
      return keysA.every(key => (a as any)[key] === (b as any)[key]);
    }

    return false;
  },
};

/**
 * Cache time configurations for different data types.
 *
 * STRATEGY:
 * - Primary: Broadcast invalidation + real-time subscriptions for instant updates
 * - Fallback: revalidateOnFocus (30s throttle) catches missed updates
 * - FALLBACK_POLLING: Only used when realtime connection is unhealthy
 *
 * Broadcast invalidation provides <100ms UI updates for any mutation.
 */
export const CACHE_TIMES = {
  // Services rarely change - cache for 5 minutes (real-time handles immediate updates)
  STATIC_DATA: 5 * 60 * 1000, // 5 minutes

  // Business settings cache for 5 minutes
  BUSINESS_DATA: 5 * 60 * 1000, // 5 minutes

  // Bookings - broadcast provides instant updates, polling DISABLED
  BOOKINGS: 0, // DISABLED - broadcast handles updates

  // Alerts - broadcast provides instant updates, polling DISABLED
  ALERTS: 0, // DISABLED - broadcast handles updates

  // Earnings - broadcast provides instant updates after payments, polling DISABLED
  EARNINGS: 0, // DISABLED - broadcast handles updates

  // Customer search results - keep short for typing
  SEARCH: 10 * 1000, // 10 seconds

  // Staff members - broadcast handles updates, polling DISABLED
  STAFF: 0, // DISABLED - broadcast handles updates

  // Fallback polling interval - only used when realtime connection appears unhealthy
  // This is a safety net, not the primary update mechanism
  FALLBACK_POLLING: 30 * 1000, // 30 seconds
} as const;

/**
 * SWR key generators for consistent cache keys.
 */
export const swrKeys = {
  // Business data
  business: (businessId: string) => ['business', businessId],
  businessHours: (businessId: string) => ['businessHours', businessId],
  businessSettings: (businessId: string) => ['businessSettings', businessId],
  
  // Services and staff (rarely change)
  services: (businessId: string) => ['services', businessId],
  allServices: (businessId: string) => ['allServices', businessId],
  staff: (businessId: string) => ['staff', businessId],
  allStaff: (businessId: string) => ['allStaff', businessId],
  staffHours: (staffId: string) => ['staffHours', staffId],
  
  // Bookings
  bookingsForDate: (businessId: string, date: string, staffId?: string | null) => 
    ['bookings', 'date', businessId, date, staffId ?? 'all'],
  bookingsForDateRange: (businessId: string, start: string, end: string, staffId?: string | null) => 
    ['bookings', 'range', businessId, start, end, staffId ?? 'all'],
  upcomingBookings: (businessId: string, limit: number) =>
    ['bookings', 'upcoming', businessId, limit],
  dueBookings: (businessId: string, limit: number) =>
    ['bookings', 'due', businessId, limit],
  barberAvailability: (businessId: string, staffId: string, date: string) =>
    ['bookings', 'availability', businessId, staffId, date],
  
  // Customers
  customers: (businessId: string) => ['customers', businessId],
  customerSearch: (businessId: string, query: string) => ['customers', 'search', businessId, query],
  customerOffers: (businessId: string, customerId: string) => ['customerOffers', businessId, customerId],
  
  // Alerts and earnings
  alerts: (businessId: string) => ['alerts', businessId],
  todayEarnings: (businessId: string) => ['earnings', 'today', businessId],
  todayEarningsForStaff: (businessId: string, staffId: string) => ['earnings', 'today', businessId, 'staff', staffId],
  
  // Inbox
  inboxMessages: (businessId: string) => ['inbox', businessId],
  unreadCount: (businessId: string) => ['inbox', 'unread', businessId],
  
  // Automations
  automationRules: (businessId: string) => ['automations', businessId],
  
  // Payments/Sales
  payments: (businessId: string) => ['payments', businessId],
  recentPayments: (businessId: string, limit: number) => ['payments', 'recent', businessId, limit],
  paymentStats: (businessId: string, startDate: string, endDate: string) => ['payments', 'stats', businessId, startDate, endDate],
  salesSummary: (businessId: string, range: string, date: string) => ['sales', 'summary', businessId, range, date],

  // Platform settings (global)
  platformSettings: () => ['platformSettings'],
} as const;

